import { catchError, pipe, when } from 'overmind';

import matchState from '@/common/utils/match';
import type { TenantIdentityProviderMapping } from '@/overmind/effects/api/gridPlannerApi';
import type { GUID } from '@/overmind/effects/auth';
import { AbortError, UnexpectedStateError } from '@/overmind/errors';
import { isUserSignedInToVolueIdentity } from '@/overmind/machines/auth';

import type { Context } from '../..';

export async function authorize(context: Context) {
  const appConfig =
    await context.effects.api.assets.getApplicationConfiguration();
  if (context.state.config.type !== 'AVAILABLE') {
    return;
  }
  const applicationId = context.state.config.config.auth.applicationId;
  const tenantId = await context.actions.tenants.getTenantForUserSession({
    applicationId: applicationId as unknown as GUID
  });

  context.actions.tenants.trySavingTenant({ tenantId });

  const tenantsMapping =
    await context.effects.api.gridPlannerApi.getTenantsMapping(tenantId as any);
  const newTenantIdentityProvider = tenantsMapping.filter(
    tenant => tenant.tenantId === tenantId
  )[0]?.identityProvider;

  const previouslySelectedTenantIdentityProvider =
    await getPreviouslySelectedIdentityProvider(
      context.effects,
      applicationId,
      tenantsMapping
    );

  clearSessionIfIdentityProviderChange(
    newTenantIdentityProvider,
    previouslySelectedTenantIdentityProvider
  );

  const acrValues = getAcrValues(newTenantIdentityProvider);

  context.effects.auth.initialize(
    {
      onSilentRenewError: error => {
        context.actions.internal.renewTokenFailed(error);
        if (error.message === 'login_required') {
          context.effects.auth.loginWhenPageVisible();
        }
      },
      onAccessTokenExpired: () => {
        context.actions.internal.accessTokenExpired();
      },
      // eslint-disable-next-line @typescript-eslint/naming-convention
      client_id: appConfig.auth.clientId,
      authority: appConfig.auth.authority,
      scope: appConfig.auth.scope
    },
    acrValues
  );
  context.actions.setAuthAsInitialized();
}

export const setAuthAsInitialized = async ({ state, actions }: Context) => {
  state.auth.send('INITIALIZE');
  await actions.authInitialized();
};

const setAuthorizationHeader = (
  { effects }: Context,
  token: string | undefined
) => {
  effects.api.orchestrator.setToken(token as string);
  effects.api.networkService.setToken(token as string);
  effects.api.identity.setToken(token as string);
};

const login = ({ effects }: Context) => effects.auth.login();

const finishAuth = async ({ effects, state }: Context) => {
  const preferredUsername = await effects.auth.getPreferredUsername();
  const identityProvider = await effects.auth.getIdentityProvider();
  const name = await effects.auth.getName();
  const token = await effects.auth.getToken();
  state.auth.send('AUTHENTICATION_SUCCESS', {
    name: name ?? '',
    preferredUsername: preferredUsername ?? '',
    token: token ?? '',
    identityProvider: identityProvider ?? ''
  });
};

const handleAuthenticationRequest = async ({ state, effects }: Context) =>
  matchState(state.auth, {
    NOT_INITIALIZED: () => {
      throw new UnexpectedStateError();
    },
    AUTHENTICATED: () => effects.auth.getToken(),
    AUTHENTICATING: () => {
      throw new AbortError();
    },
    AUTHENTICATING_ARCGIS: () => {
      throw new AbortError();
    },
    AWAITING_ARCGIS_AUTHENTICATION: () => {
      throw new AbortError();
    },
    ERROR: () => {
      throw new UnexpectedStateError();
    },
    UNAUTHENTICATED: () => {
      state.auth.send('AUTHENTICATE');
      return effects.auth.getToken();
    },
    AWAITING_FEATURE_TOGGLES: () => {
      throw new AbortError();
    },
    FEATURE_TOGGLES_IN_PROGRESS: () => {
      throw new AbortError();
    }
  });

const isAuthenticated = (
  _: any,
  token: Awaited<ReturnType<typeof handleAuthenticationRequest>>
) => Boolean(token);
export const authInitialized = pipe(
  handleAuthenticationRequest,
  when(isAuthenticated, {
    true: pipe(setAuthorizationHeader, finishAuth),
    false: login
  }),
  catchError((context: Context, error) => {
    if (error instanceof AbortError) {
      return;
    }
    if (!(error instanceof UnexpectedStateError)) {
      if(error.message === 'login_required') {
        context.effects.auth.loginWhenPageVisible();
        return
      }
      context.state.auth.send('AUTHENTICATION_ERROR', error.message);
    }
  })
);

export const loggedOut = ({ state, effects }: Context) => {
  const authorizationState = state.auth.send('SIGN_OUT');

  if (isUserSignedInToVolueIdentity(authorizationState)) {
    effects.auth.logout();
  }
};
function getAcrValues(newTenantIdentityProvider: string) {
  if (newTenantIdentityProvider === undefined) {
    newTenantIdentityProvider = getDefaultIdentityProvider();
  }

  const acrValues = 'idp:' + newTenantIdentityProvider;
  return acrValues;
}

function getDefaultIdentityProvider() {
  const defaultIdentityProvider = 'AAD';
  return defaultIdentityProvider;
}

async function getPreviouslySelectedIdentityProvider(
  effects: Context['effects'],
  applicationId: string,
  tenantsConfigurations: TenantIdentityProviderMapping
) {
  const previouslySelectedTenant = (
    await effects.selectedTenant.readPreviouslySelected({ applicationId })
  ).toGuid();
  const previouslySelectedTenantIdentityProvider = tenantsConfigurations.filter(
    tenant => tenant.tenantId === previouslySelectedTenant
  )[0]?.identityProvider;
  return previouslySelectedTenantIdentityProvider;
}

function clearSessionIfIdentityProviderChange(
  newTenantIdentityProvider: string,
  previouslySelectedTenantIdentityProvider: string
) {
  if (newTenantIdentityProvider !== previouslySelectedTenantIdentityProvider) {
    sessionStorage.clear();
  }
}
